<script lang="ts" name="Badge" setup>
const props = withDefaults(
  defineProps<{
    color?: UIColor
    button?: boolean
    variant?: "solid" | "outline"
  }>(),
  {
    color: "rnxblue",
    button: false,
    variant: "solid",
  },
)

const emit = defineEmits(["delete:badge"])

// https://tailwindcss.com/docs/content-configuration#dynamic-class-names
const badgeColorMap = {
  rnxblue: {
    wrapper: {
      solid: "bg-rnxblue-50 text-rnxblue-600",
      outline: "border border-rnxblue-400 text-rnxblue-600 bg-transparent",
    },
    dot: "bg-rnxblue-400",
    button: "hover:bg-rnxblue-200 hover:text-rnxblue-700",
  },
  rnxgreen: {
    wrapper: {
      solid: "bg-rnxgreen-50 text-rnxgreen-700",
      outline: "border border-rnxgreen-500 text-rnxgreen-700 bg-transparent",
    },
    dot: "bg-rnxgreen-500",
    button: "hover:bg-rnxgreen-200 hover:text-rnxgreen-500",
  },
  rnxorange: {
    wrapper: {
      solid: "bg-rnxorange-50 text-rnxorange-500",
      outline: "border border-rnxorange-400 text-rnxorange-500 bg-transparent",
    },
    dot: "bg-rnxorange-400",
    button: "hover:bg-orange-200 hover:text-orange-500",
  },
  orange: {
    wrapper: {
      solid: "bg-orange-50 text-orange-500",
      outline: "border border-orange-400 text-orange-500 bg-transparent",
    },
    dot: "bg-orange-400",
    button: "hover:bg-orange-200 hover:text-orange-500",
  },
  blue: {
    wrapper: {
      solid: "bg-blue-100 text-blue-700",
      outline: "border border-blue-500 text-blue-700 bg-transparent",
    },
    dot: "bg-blue-500",
    button: "hover:bg-blue-200 hover:text-blue-500",
  },
  gray: {
    wrapper: {
      solid: "bg-gray-100 text-gray-700",
      outline: "border border-gray-500 text-gray-700 bg-transparent",
    },
    dot: "bg-gray-500",
    button: "hover:bg-gray-200 hover:text-gray-500",
  },
  dark: {
    wrapper: {
      solid: "bg-gray-200 text-gray-700",
      outline: "border border-gray-500 text-gray-700 bg-transparent",
    },
    dot: "bg-gray-500",
    button: "hover:bg-gray-300 hover:text-gray-600",
  },
  zinc: {
    wrapper: {
      solid: "bg-zinc-100 text-zinc-700",
      outline: "border border-zinc-500 text-zinc-700 bg-transparent",
    },
    dot: "bg-zinc-500",
    button: "hover:bg-zinc-200 hover:text-zinc-500",
  },
  green: {
    wrapper: {
      solid: "bg-green-100 text-green-700",
      outline: "border border-green-500 text-green-700 bg-transparent",
    },
    dot: "bg-green-500",
    button: "hover:bg-green-200 hover:text-green-500",
  },
  indigo: {
    wrapper: {
      solid: "bg-indigo-100 text-indigo-700",
      outline: "border border-indigo-500 text-indigo-700 bg-transparent",
    },
    dot: "bg-indigo-500",
    button: "hover:bg-indigo-200 hover:text-indigo-500",
  },
  purple: {
    wrapper: {
      solid: "bg-purple-100 text-purple-700",
      outline: "border border-purple-500 text-purple-700 bg-transparent",
    },
    dot: "bg-purple-500",
    button: "hover:bg-purple-200 hover:text-purple-500",
  },
  sky: {
    wrapper: {
      solid: "bg-sky-100 text-sky-700",
      outline: "border border-sky-500 text-sky-700 bg-transparent",
    },
    dot: "bg-sky-500",
    button: "hover:bg-sky-200 hover:text-sky-500",
  },
  cyan: {
    wrapper: {
      solid: "bg-cyan-100 text-cyan-700",
      outline: "border border-cyan-500 text-cyan-700 bg-transparent",
    },
    dot: "bg-cyan-500",
    button: "hover:bg-cyan-200 hover:text-cyan-500",
  },
  stone: {
    wrapper: {
      solid: "bg-stone-100 text-stone-700",
      outline: "border border-stone-500 text-stone-700 bg-transparent",
    },
    dot: "bg-stone-500",
    button: "hover:bg-stone-200 hover:text-stone-500",
  },
  slate: {
    wrapper: {
      solid: "bg-slate-100 text-slate-700",
      outline: "border border-slate-500 text-slate-700 bg-transparent",
    },
    dot: "bg-slate-500",
    button: "hover:bg-slate-200 hover:text-slate-500",
  },
  red: {
    wrapper: {
      solid: "bg-red-100 text-red-700",
      outline: "border border-red-500 text-red-700 bg-transparent",
    },
    dot: "bg-red-500",
    button: "hover:bg-red-200 hover:text-red-500",
  },
  yellow: {
    wrapper: {
      solid: "bg-yellow-50 text-yellow-500",
      outline: "border border-yellow-400 text-yellow-500 bg-transparent",
    },
    dot: "bg-yellow-400",
    button: "hover:bg-yellow-100 hover:text-yellow-400",
  },
  teal: {
    wrapper: {
      solid: "bg-teal-100 text-teal-700",
      outline: "border border-teal-500 text-teal-700 bg-transparent",
    },
    dot: "bg-teal-500",
    button: "hover:bg-teal-200 hover:text-teal-500",
  },
  lime: {
    wrapper: {
      solid: "bg-lime-100 text-lime-700",
      outline: "border border-lime-500 text-lime-700 bg-transparent",
    },
    dot: "bg-lime-500",
    button: "hover:bg-lime-200 hover:text-lime-500",
  },
  emerald: {
    wrapper: {
      solid: "bg-emerald-100 text-emerald-700",
      outline: "border border-emerald-500 text-emerald-700 bg-transparent",
    },
    dot: "bg-emerald-500",
    button: "hover:bg-emerald-200 hover:text-emerald-500",
  },
  amber: {
    wrapper: {
      solid: "bg-amber-100 text-amber-700",
      outline: "border border-amber-500 text-amber-700 bg-transparent",
    },
    dot: "bg-amber-500",
    button: "hover:bg-amber-200 hover:text-amber-500",
  },
  pink: {
    wrapper: {
      solid: "bg-pink-100 text-pink-700",
      outline: "border border-pink-500 text-pink-700 bg-transparent",
    },
    dot: "bg-pink-500",
    button: "hover:bg-pink-200 hover:text-pink-500",
  },
  violet: {
    wrapper: {
      solid: "bg-violet-100 text-violet-700",
      outline: "border border-violet-500 text-violet-700 bg-transparent",
    },
    dot: "bg-violet-500",
    button: "hover:bg-violet-200 hover:text-violet-500",
  },
  rose: {
    wrapper: {
      solid: "bg-rose-100 text-rose-700",
      outline: "border border-rose-500 text-rose-700 bg-transparent",
    },
    dot: "bg-rose-500",
    button: "hover:bg-rose-200 hover:text-rose-500",
  },
} as const

const style = computed(() => {
  const isMapped = Object.keys(badgeColorMap).includes(props.color)
  if (!isMapped) {
    console.debug(`'${props.color}' is not mapped. Fallback to 'gray'.`)
  }
  const color = isMapped ? props.color : "gray"
  const colorMap = badgeColorMap[color as keyof typeof badgeColorMap]
  return {
    wrapper: colorMap.wrapper[props.variant],
    dot: colorMap.dot,
    button: colorMap.button,
  }
})

const deleteBadge = () => emit("delete:badge")
</script>

<template>
  <div
    class="inline-flex min-h-5 items-center gap-x-1.5 whitespace-nowrap rounded-full px-1.5 text-sm font-medium"
    :class="[style.wrapper, 'min-w-0 max-w-full']"
  >
    <div v-if="variant === 'outline'" class="size-2 shrink-0 rounded-full" :class="style.dot" />
    <!-- `truncate` (a block box) keeps text-overflow:ellipsis working; `align-middle`
         on element children re-centers inline content (e.g. an icon+text row) that
         would otherwise sit on the baseline and add a descender gap. -->
    <span class="min-w-0 truncate [&>*]:align-middle"><slot name="default" /></span>

    <UButton
      v-if="button"
      :color="color"
      icon="i-heroicons-x-mark-20-solid"
      variant="ghost"
      class="size-3.5 px-0"
      :class="style.button"
      size="sm"
      :ui="{
        rounded: 'rounded-full',
        icon: { size: { sm: 'w-3.5 h-3.5' } },
      }"
      @click="deleteBadge"
    />
  </div>
</template>
